import { useState } from 'react';
import type { MenuItemDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';

import { labels } from '../labels.js';
import { Icon } from './Icon.js';
import { QuantityStepper } from './QuantityStepper.js';
import styles from './MenuItemCard.module.css';

interface MenuItemCardProps {
  item: MenuItemDTO;
  quantity: number;
  onAdd: () => void;
  onQuantityChange: (quantity: number) => void;
}

/**
 * Dish row -- Design System v1.0, menu screen 03: thumbnail, name, short
 * description, price in FCFA. docs/15 section 3.2: no empty frame without a
 * photo, a sold-out dish is dimmed and cannot be added.
 */
export function MenuItemCard({ item, quantity, onAdd, onQuantityChange }: MenuItemCardProps) {
  const [broken, setBroken] = useState(false);
  const showPhoto = Boolean(item.imageUrl) && !broken;

  return (
    <article className={`${styles.card} ${item.isAvailable ? '' : styles.off} ${quantity > 0 ? styles.inCart : ''}`}>
      {showPhoto && (
        <img
          src={item.imageUrl ?? undefined}
          alt={item.name}
          loading="lazy"
          className={styles.photo}
          onError={() => setBroken(true)}
        />
      )}

      <div className={styles.body}>
        <h3 className={styles.name}>{item.name}</h3>
        {item.description && <p className={styles.description}>{item.description}</p>}
        <div className={styles.footer}>
          <span className={`price ${styles.price}`}>{formatMoney(item.priceMinor, item.currency)}</span>
          {!item.isAvailable ? (
            <span className={styles.soldOut}>{labels.menu.unavailable}</span>
          ) : quantity > 0 ? (
            <QuantityStepper name={item.name} quantity={quantity} onChange={onQuantityChange} />
          ) : (
            <button type="button" className={styles.add} onClick={onAdd} aria-label={labels.menu.addOne(item.name)}>
              <Icon name="plus" size={20} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
